import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/shared/db";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  LockClosedIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";

export const UpdatePasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Check if we have the necessary params from Supabase
  useEffect(() => {
    const accessToken = searchParams.get("access_token");
    const type = searchParams.get("type");

    // If we have an access token from the email link, set it
    if (accessToken && type === "recovery") {
      supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: searchParams.get("refresh_token") || "",
      });
    }
  }, [searchParams]);

  const validatePassword = (pass: string): string | null => {
    if (pass.length < 8) {
      return t("auth.updatePassword.validation.minLength");
    }
    if (!/[A-Z]/.test(pass)) {
      return t("auth.updatePassword.validation.uppercase");
    }
    if (!/[a-z]/.test(pass)) {
      return t("auth.updatePassword.validation.lowercase");
    }
    if (!/[0-9]/.test(pass)) {
      return t("auth.updatePassword.validation.number");
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (password !== confirmPassword) {
      setError(t("auth.updatePassword.errors.passwordMismatch"));
      return;
    }

    // Validate password strength
    const validationError = validatePassword(password);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) throw updateError;

      setSuccess(true);
      toast.success(t("auth.updatePassword.success"));

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/auth", { replace: true });
      }, 3000);
    } catch (err) {
      console.error("Error updating password:", err);
      setError(
        err instanceof Error
          ? err.message
          : t("auth.updatePassword.errors.generic")
      );
      toast.error(t("auth.updatePassword.errors.generic"));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="relative min-h-screen bg-gradient-hero flex items-center justify-center p-4 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-tiber-500/20 blur-3xl" />
          <div className="absolute bottom-1/4 -left-32 w-80 h-80 rounded-full bg-tiber-400/15 blur-3xl" />
        </div>
        <div className="absolute inset-0 opacity-20 overflow-hidden pointer-events-none">
          <div className="absolute top-1/5 left-0 w-full h-px bg-linear-to-r from-transparent via-white/40 to-transparent animate-pulse" />
          <div
            className="absolute top-2/5 left-0 w-full h-px bg-linear-to-r from-transparent via-white/30 to-transparent animate-pulse"
            style={{ animationDelay: "0.8s" }}
          />
          <div
            className="absolute top-3/5 left-0 w-full h-px bg-linear-to-r from-transparent via-white/35 to-transparent animate-pulse"
            style={{ animationDelay: "1.6s" }}
          />
          <div
            className="absolute top-4/5 left-0 w-full h-px bg-linear-to-r from-transparent via-white/25 to-transparent animate-pulse"
            style={{ animationDelay: "2.4s" }}
          />
          <div className="absolute inset-0">
            <div
              className="absolute top-1/3 left-0 w-[200%] h-px bg-linear-to-r from-transparent via-white/25 to-transparent rotate-12 origin-left"
              style={{ animation: "slide 18s linear infinite" }}
            />
            <div
              className="absolute top-2/3 left-0 w-[200%] h-px bg-linear-to-r from-transparent via-white/30 to-transparent -rotate-12 origin-left"
              style={{
                animation: "slide-reverse 22s linear infinite",
                animationDelay: "3s",
              }}
            />
          </div>
          <div
            className="absolute top-0 left-1/5 w-px h-full bg-linear-to-b from-transparent via-white/20 to-transparent animate-pulse"
            style={{ animationDelay: "0.3s" }}
          />
          <div
            className="absolute top-0 left-1/2 w-px h-full bg-linear-to-b from-transparent via-white/25 to-transparent animate-pulse"
            style={{ animationDelay: "1.2s" }}
          />
          <div
            className="absolute top-0 left-4/5 w-px h-full bg-linear-to-b from-transparent via-white/20 to-transparent animate-pulse"
            style={{ animationDelay: "1.8s" }}
          />
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 text-center"
        >
          <div className="w-20 h-20 bg-atlantis-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircleIcon className="w-12 h-12 text-atlantis-600" />
          </div>
          <h2 className="text-2xl font-bold text-tiber-900 mb-4">
            {t("auth.updatePassword.successTitle")}
          </h2>
          <p className="text-tiber-600 mb-6">
            {t("auth.updatePassword.successMessage")}
          </p>
          <p className="text-sm text-tiber-500">
            {t("auth.updatePassword.redirecting")}
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gradient-hero flex items-center justify-center p-4 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-tiber-500/20 blur-3xl" />
        <div className="absolute bottom-1/4 -left-32 w-80 h-80 rounded-full bg-tiber-400/15 blur-3xl" />
      </div>
      <div className="absolute inset-0 opacity-20 overflow-hidden pointer-events-none">
        <div className="absolute top-1/5 left-0 w-full h-px bg-linear-to-r from-transparent via-white/40 to-transparent animate-pulse" />
        <div
          className="absolute top-2/5 left-0 w-full h-px bg-linear-to-r from-transparent via-white/30 to-transparent animate-pulse"
          style={{ animationDelay: "0.8s" }}
        />
        <div
          className="absolute top-3/5 left-0 w-full h-px bg-linear-to-r from-transparent via-white/35 to-transparent animate-pulse"
          style={{ animationDelay: "1.6s" }}
        />
        <div
          className="absolute top-4/5 left-0 w-full h-px bg-linear-to-r from-transparent via-white/25 to-transparent animate-pulse"
          style={{ animationDelay: "2.4s" }}
        />
        <div className="absolute inset-0">
          <div
            className="absolute top-1/3 left-0 w-[200%] h-px bg-linear-to-r from-transparent via-white/25 to-transparent rotate-12 origin-left"
            style={{ animation: "slide 18s linear infinite" }}
          />
          <div
            className="absolute top-2/3 left-0 w-[200%] h-px bg-linear-to-r from-transparent via-white/30 to-transparent -rotate-12 origin-left"
            style={{
              animation: "slide-reverse 22s linear infinite",
              animationDelay: "3s",
            }}
          />
        </div>
        <div
          className="absolute top-0 left-1/5 w-px h-full bg-linear-to-b from-transparent via-white/20 to-transparent animate-pulse"
          style={{ animationDelay: "0.3s" }}
        />
        <div
          className="absolute top-0 left-1/2 w-px h-full bg-linear-to-b from-transparent via-white/25 to-transparent animate-pulse"
          style={{ animationDelay: "1.2s" }}
        />
        <div
          className="absolute top-0 left-4/5 w-px h-full bg-linear-to-b from-transparent via-white/20 to-transparent animate-pulse"
          style={{ animationDelay: "1.8s" }}
        />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="/assets/logo.svg"
            alt="Lawgentia"
            className="h-12 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-white">
            {t("auth.updatePassword.title")}
          </h1>
          <p className="text-tiber-200 mt-2">
            {t("auth.updatePassword.subtitle")}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-linear-to-r from-tiber-600 to-tiber-700 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <LockClosedIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-semibold text-white">
                  {t("auth.updatePassword.formTitle")}
                </h2>
                <p className="text-tiber-100 text-sm">
                  {t("auth.updatePassword.formSubtitle")}
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl"
              >
                <ExclamationTriangleIcon className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </motion.div>
            )}

            {/* New Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-tiber-900 mb-2"
              >
                {t("auth.updatePassword.newPassword")}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 border-2 border-tiber-200 rounded-xl 
                           focus:ring-2 focus:ring-tiber-500 focus:border-tiber-500 
                           transition-colors text-tiber-900"
                  placeholder="••••••••"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-tiber-400 hover:text-tiber-600"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="w-5 h-5" />
                  ) : (
                    <EyeIcon className="w-5 h-5" />
                  )}
                </button>
              </div>
              <p className="mt-2 text-xs text-tiber-500">
                {t("auth.updatePassword.passwordRequirements")}
              </p>
            </div>

            {/* Confirm Password */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-tiber-900 mb-2"
              >
                {t("auth.updatePassword.confirmPassword")}
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 border-2 border-tiber-200 rounded-xl 
                           focus:ring-2 focus:ring-tiber-500 focus:border-tiber-500 
                           transition-colors text-tiber-900"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-tiber-400 hover:text-tiber-600"
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="w-5 h-5" />
                  ) : (
                    <EyeIcon className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !password || !confirmPassword}
              className="w-full py-3.5 px-4 bg-linear-to-r from-tiber-600 to-tiber-700 
                       text-white font-semibold rounded-xl shadow-lg
                       hover:from-tiber-700 hover:to-tiber-800 
                       focus:ring-4 focus:ring-tiber-500/30
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-all duration-200"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {t("auth.updatePassword.updating")}
                </span>
              ) : (
                t("auth.updatePassword.submit")
              )}
            </button>

            {/* Back to Login */}
            <p className="text-center text-sm text-tiber-600">
              <button
                type="button"
                onClick={() => navigate("/auth")}
                className="text-tiber-700 hover:text-tiber-900 font-medium underline"
              >
                {t("auth.updatePassword.backToLogin")}
              </button>
            </p>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-tiber-300 text-sm mt-6">
          © {new Date().getFullYear()} Lawgentia.{" "}
          {t("common.allRightsReserved")}
        </p>
      </motion.div>
    </div>
  );
};

export default UpdatePasswordPage;
